import axios, { AxiosError } from 'axios';
import { TypedApiClient, type ReplayableRequestConfig } from './index';

/**
 * ATL-1503 — obtain a write request EXACTLY as `TypedApiClient.request()` would issue it, without
 * issuing it: same endpoint path, same date serialization and body sanitizing, same pre-flight
 * request-schema validation (an invalid body is refused here, as it would be live).
 *
 * The dual-write outbox needs this to queue a write BEHIND writes already queued for the same
 * document instead of sending it past them — a preconditioned supply payment update that overtakes
 * an older queued one is refused, or the older one is later applied over it. `request()` already
 * builds the replayable config for every write that reaches the transport; a transport that refuses
 * without sending makes it produce that config and nothing else. The capturing client is its own
 * instance, so no request of the live client can ever be caught by it.
 */
const CAPTURED = 'ERR_ATL1503_REQUEST_CAPTURED';

let capturingClient: TypedApiClient | null = null;

function client(): TypedApiClient {
  capturingClient ??= new TypedApiClient({
    // Never used: the adapter answers before anything is sent.
    baseURL: 'http://request-capture.invalid',
    getAuthToken: async () => null,
    adapter: async (config) => {
      throw new AxiosError('request captured, not sent (ATL-1503)', CAPTURED, config);
    },
  });
  return capturingClient;
}

/** The write never reached the transport — e.g. a client-side validation refusal. */
export class RequestNotCapturedError extends Error {
  constructor(public readonly reason: unknown) {
    super(`request was not captured: ${reason instanceof Error ? reason.message : String(reason)}`);
    this.name = 'RequestNotCapturedError';
  }
}

/**
 * `send` issues ONE write through the given client — normally an endpoint factory's method, e.g.
 * `(c) => createSuppliesApi(c).updateSupply(id, dto)`. Resolves with the request that write would
 * have sent; rejects with `RequestNotCapturedError` when it never reached the transport (`reason`
 * is what the live call would have thrown).
 */
export async function captureWriteRequest(
  send: (client: TypedApiClient) => Promise<unknown>,
): Promise<ReplayableRequestConfig> {
  try {
    await send(client());
  } catch (error) {
    const requestConfig = (error as { requestConfig?: ReplayableRequestConfig })?.requestConfig;
    if (axios.isAxiosError(error) && error.code === CAPTURED && requestConfig) {
      return requestConfig;
    }
    throw new RequestNotCapturedError(error);
  }
  throw new RequestNotCapturedError(new Error('the write resolved without reaching the transport'));
}
